import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  List,
  ListItem,
  ListItemText,
  Paper,
  Grid,
  AppBar,
  Toolbar,
  Button,
  TextField,
  Box,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { BrowserPhone } from '../components/BrowserPhone';
import { getContacts, startCall, getCalls, createContact } from '../services/api';

interface Contact {
  id: number;
  name: string;
  phone_number: string;
  company_name: string;
}

interface Call {
  id: number;
  contact_name: string;
  start_time: string;
  status: string;
}

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [calls, setCalls] = useState<Call[]>([]);
  const [newName, setNewName] = useState('');
  const [newNumber, setNewNumber] = useState('');
  const [newCompany, setNewCompany] = useState('');

  useEffect(() => {
    loadContacts();
    loadCalls();
  }, []);

  const loadContacts = async () => {
    try {
      const response = await getContacts();
      setContacts(response.results);
    } catch (error) {
      console.error('Error loading contacts:', error);
    }
  };

  const loadCalls = async () => {
    try {
      const response = await getCalls();
      setCalls(response);
    } catch (error) {
      console.error('Error loading calls:', error);
    }
  };

  const handleCall = async (contactId: number) => {
    try {
      await startCall(contactId);
      loadCalls(); // Refresh calls list
    } catch (error) {
      console.error('Error making call:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            VOIP Sales Dashboard
          </Typography>
          <Button color="inherit" onClick={handleLogout}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Contacts
            </Typography>
            <Box sx={{ mb: 3 }}>
              <TextField
                label="Name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                fullWidth
                margin="normal"
              />
              <TextField
                label="Phone Number"
                value={newNumber}
                onChange={(e) => setNewNumber(e.target.value)}
                fullWidth
                margin="normal"
              />
              <TextField
                label="Company Name"
                value={newCompany}
                onChange={(e) => setNewCompany(e.target.value)}
                fullWidth
                margin="normal"
              />
              <Button
                variant="contained"
                color="primary"
                fullWidth
                sx={{ mt: 2 }}
                onClick={async () => {
                  try {
                    await createContact(newName, newNumber, newCompany);
                    setNewName('');
                    setNewNumber('');
                    setNewCompany('');
                    loadContacts(); // Refresh the contacts list
                  } catch (error) {
                    console.error('Error creating contact:', error);
                  }
                }}
              >
                Add Contact
              </Button>
            </Box>
            <List>
              {contacts.map((contact) => (
                <ListItem
                  key={contact.id}
                  secondaryAction={
                    <BrowserPhone
                      contactNumber={contact.phone_number}
                      onCallEnd={() => loadCalls()}
                    />
                  }
                >
                  <ListItemText
                    primary={contact.name}
                    secondary={`${contact.company_name} - ${contact.phone_number}`}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Recent Calls
            </Typography>
            <List>
              {(calls || []).map((call) => (
                <ListItem key={call.id}>
                  <ListItemText
                    primary={call.contact_name}
                    secondary={`${new Date(call.start_time).toLocaleString()} - ${
                      call.status
                    }`}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Container>
    </>
  );
};
